import { computed, onUnmounted, ref } from 'vue-demi';
import type { ComputedRef, Ref } from 'vue-demi';
import t from '../render/utils/i18n';

/** D-25: 飞书 Uy4Ewc1s5iqbWYkU51Nc7l9tnvd 明确 60s 频限 */
const COOLDOWN_SECONDS = 60;

export type UseSmsCountdownReturn = {
  remaining: ComputedRef<number>;
  isCountingDown: ComputedRef<boolean>;
  /** RH-B4:请求期间为 true,阻止连点重复发短信 */
  smsSending: ComputedRef<boolean>;
  /** RH-B4:canSend = !isCountingDown && !smsSending(号码合法性由上层组合判断) */
  canSend: ComputedRef<boolean>;
  btnLabel: ComputedRef<string>;
  start(): void;
  stop(): void;
  /** useLottery.sendSms 请求开始/结束调用 */
  markSending(value: boolean): void;
};

export function useSmsCountdown(): UseSmsCountdownReturn {
  const startAt: Ref<number | null> = ref(null);
  const now = ref(Date.now());
  const sending: Ref<boolean> = ref(false);
  let timerId: ReturnType<typeof setInterval> | null = null;

  const remaining = computed(() => {
    if (startAt.value === null) return 0;
    return Math.max(
      0,
      COOLDOWN_SECONDS - Math.floor((now.value - startAt.value) / 1000),
    );
  });

  const isCountingDown = computed(() => remaining.value > 0);
  const smsSending = computed(() => sending.value);
  // RH-B4:合并 smsSending 到 canSend
  const canSend = computed(() => !isCountingDown.value && !smsSending.value);

  const btnLabel = computed(() => {
    if (smsSending.value) return 'Sending...';
    if (isCountingDown.value) {
      return t('amoe.sms.cooldown').replace('{n}', String(remaining.value));
    }
    return t('amoe.sms.sendBtn');
  });

  function tick(): void {
    now.value = Date.now();
    if (remaining.value <= 0) stop();
  }

  function start(): void {
    startAt.value = Date.now();
    now.value = Date.now();
    if (timerId !== null) clearInterval(timerId);
    timerId = setInterval(tick, 1000);
  }

  function stop(): void {
    if (timerId !== null) {
      clearInterval(timerId);
      timerId = null;
    }
    startAt.value = null;
  }

  function markSending(value: boolean): void {
    sending.value = value;
  }

  // Pitfall 5: 背景被浏览器节流,切回后强制重算
  function onVisibilityChange(): void {
    if (!document.hidden) now.value = Date.now();
  }
  document.addEventListener('visibilitychange', onVisibilityChange);

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (timerId !== null) clearInterval(timerId);
  });

  return {
    remaining,
    isCountingDown,
    smsSending,
    canSend,
    btnLabel,
    start,
    stop,
    markSending,
  };
}
